import { useId, type ReactNode, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";
import { restrainedSpring, TextShimmer } from "./MotionPrimitives";

export function CastingStepHeader({ current, title, onBack, onClose }: { current: number; title: string; onBack?: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  return <header className="casting-step-header">
    <div className="step-header-row">
      {onBack ? <button type="button" className="icon-button" onClick={onBack} aria-label={t("common.back")}><span aria-hidden="true">←</span></button> : <span className="step-header-spacer" />}
      <div><p>{t("cast.stepCount", { current, total: 4 })}</p><h2>{title}</h2></div>
      <button type="button" className="icon-button" onClick={onClose} aria-label={t("cast.exit")}><span aria-hidden="true">×</span></button>
    </div>
    <div className="step-progress" role="progressbar" aria-label={t("cast.progressLabel")} aria-valuemin={1} aria-valuemax={4} aria-valuenow={current}>
      {Array.from({ length: 4 }, (_, index) => <span key={index} className={index < current ? "done" : ""}>{index === current - 1 && <motion.i layoutId="casting-mobile-progress" transition={reduceMotion ? { duration: 0 } : restrainedSpring} />}</span>)}
    </div>
  </header>;
}

export function StickyActionBar({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`sticky-action-bar ${className}`.trim()}>{children}</div>;
}

export function ActionDock({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`action-dock ${className}`.trim()}>{children}</div>;
}

export function MobilePageHeader({ eyebrow, title, body, backTo, backLabel, action }: { eyebrow?: string; title: string; body?: ReactNode; backTo?: string; backLabel?: string; action?: ReactNode }) {
  return <header className="page-header mobile-page-header">
    {backTo && <Link className="icon-button page-back" to={backTo} aria-label={backLabel}><span aria-hidden="true">←</span></Link>}
    <div>{eyebrow && <p className="eyebrow">{eyebrow}</p>}<h1>{title}</h1>{body && <div className="page-header-body">{body}</div>}</div>
    {action && <div className="page-header-action">{action}</div>}
  </header>;
}

export function FeedbackBanner({ tone = "info", title, children }: { tone?: "info" | "success" | "warning" | "error"; title?: string; children: ReactNode }) {
  return <div className={`feedback-banner ${tone}`} role={tone === "error" ? "alert" : "status"}>
    <span className="feedback-mark" aria-hidden="true">{tone === "success" ? "✓" : tone === "error" ? "!" : tone === "warning" ? "△" : "i"}</span>
    <div>{title && <strong>{title}</strong>}<div>{children}</div></div>
  </div>;
}

export function DisclosureSection({ title, summary, children, open = false }: { title: string; summary?: string; children: ReactNode; open?: boolean }) {
  return <details className="disclosure-section" open={open}>
    <summary><span><strong>{title}</strong>{summary && <small>{summary}</small>}</span><span aria-hidden="true">＋</span></summary>
    <div className="disclosure-content">{children}</div>
  </details>;
}

export function PageState({ kind, title, body, action, ref }: { kind: "loading" | "empty" | "error"; title: string; body?: string; action?: { label: string; to?: string; onClick?: () => void }; ref?: Ref<HTMLDivElement> }) {
  const id = useId();
  return <div className={`page-state ${kind}`} role={kind === "error" ? "alert" : "status"} aria-labelledby={id} aria-live={kind === "loading" ? "polite" : undefined} aria-busy={kind === "loading" || undefined} ref={ref}>
    <span className="state-mark" aria-hidden="true">{kind === "loading" ? <><i /><i /><i /></> : kind === "empty" ? "☷" : "!"}</span>
    <h2 id={id}>{kind === "loading" ? <TextShimmer>{title}</TextShimmer> : title}</h2>
    {body && <p>{body}</p>}
    {action && (action.to ? <Link className="button secondary" to={action.to}>{action.label}</Link> : <button type="button" className="button secondary" onClick={action.onClick}>{action.label}</button>)}
  </div>;
}
